import { cn } from "@/shared/lib/utils";

interface BulletBand {
  to: number;
  label: string;
}

interface SectionBulletProps {
  title: string;
  value: number;
  target: number;
  bands: BulletBand[]; // qualitative bands; "to" is the upper bound of each
  unit?: string;
}

/**
 * Value-vs-target chart in the Tufte bullet shape. Qualitative bands behind
 * (light → dark muted), a thin actual-value bar over them, and a target tick.
 */
export function SectionBullet({ title, value, target, bands, unit }: SectionBulletProps) {
  const max = Math.max(target, value, ...bands.map((b) => b.to), 1);
  const widthPct = (n: number) => `${Math.min(100, (n / max) * 100)}%`;

  const bandColors = ["bg-muted/30", "bg-muted/50", "bg-muted/70"];

  return (
    <div className="px-5 py-4 space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium text-foreground">{title}</span>
        <span className="text-sm tabular-nums text-muted-foreground">
          {value.toLocaleString()}
          {unit ? <span className="text-muted-foreground"> {unit}</span> : null}
          <span className="px-2 text-muted-foreground/50">/</span>
          <span className="text-foreground">
            {target.toLocaleString()}
            {unit ? <span className="text-muted-foreground"> {unit}</span> : null}
          </span>
        </span>
      </div>
      <div className="relative h-6 w-full rounded-sm bg-muted/10 overflow-hidden">
        {bands.map((b, i) => (
          <div
            key={i}
            className={cn("absolute inset-y-0 left-0", bandColors[i % bandColors.length])}
            style={{ width: widthPct(b.to) }}
            title={b.label}
          />
        ))}
        <div
          className="absolute inset-y-1.5 left-0 bg-primary rounded-sm"
          style={{ width: widthPct(value) }}
        />
        <div
          className="absolute inset-y-0 w-0.5 bg-foreground"
          style={{ left: `calc(${widthPct(target)} - 1px)` }}
          aria-label="Target"
        />
      </div>
    </div>
  );
}
